import { useState } from "react";
import { Box, Button, Chip, Link, Typography } from "@mui/material";
import IpText from "./IpText.tsx";
import { alertSummary, eventLabel, NO_EVENTS } from "@/utils/alertCopy.ts";
import { countryName } from "@/utils/countryName.ts";
import { formatDate, formatTime } from "@/utils/formatDateTime.ts";
import type { AccountEvent, Alert } from "@/types";

const LATEST_N = 50;

type Entry = { kind: "event"; at: number; event: AccountEvent } | { kind: "alert"; at: number; alert: Alert };

function entriesByDay(events: readonly AccountEvent[], alerts: readonly Alert[]): [string, Entry[]][] {
  const entries: Entry[] = [
    ...events.map((event): Entry => ({ kind: "event", at: event.at, event })),
    ...alerts.map((alert): Entry => ({ kind: "alert", at: alert.first_seen, alert })),
  ].sort((a, b) => b.at - a.at);
  const days = new Map<string, Entry[]>();
  for (const entry of entries) {
    const day = formatDate(entry.at);
    days.set(day, [...(days.get(day) ?? []), entry]);
  }
  return [...days.entries()];
}

function EventLine({ event }: { readonly event: AccountEvent }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "48px 1fr 130px 110px", gap: 1, py: 0.25 }}>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {formatTime(event.at)}
      </Typography>
      <Typography variant="body2">{eventLabel(event.outcome)}</Typography>
      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
        <IpText ip={event.ip} />
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {event.country ? countryName(event.country) : "—"}
      </Typography>
    </Box>
  );
}

function AlertMarker({
  alert,
  isCurrent,
  onOpen,
}: {
  readonly alert: Alert;
  readonly isCurrent: boolean;
  readonly onOpen: (id: string) => void;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
      <Typography variant="body2" sx={{ color: "text.secondary", width: 48 }}>
        {formatTime(alert.first_seen)}
      </Typography>
      <Chip size="small" color="warning" label={isCurrent ? "This alert" : "Alert"} />
      {isCurrent ? (
        <Typography variant="body2">{alertSummary(alert)}</Typography>
      ) : (
        <Link component="button" variant="body2" onClick={() => onOpen(alert.id)}>
          {alertSummary(alert)}
        </Link>
      )}
    </Box>
  );
}

function DayGroup({
  day,
  entries,
  currentAlertId,
  onOpenAlert,
}: {
  readonly day: string;
  readonly entries: readonly Entry[];
  readonly currentAlertId: string;
  readonly onOpenAlert: (id: string) => void;
}) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
        {day}
      </Typography>
      {entries.map((entry, i) =>
        entry.kind === "alert" ? (
          <AlertMarker
            key={`alert:${entry.alert.id}`}
            alert={entry.alert}
            isCurrent={entry.alert.id === currentAlertId}
            onOpen={onOpenAlert}
          />
        ) : (
          <EventLine key={`event:${i}`} event={entry.event} />
        ),
      )}
    </Box>
  );
}

export default function AccountTimeline({
  events,
  alerts,
  currentAlertId,
  onOpenAlert,
}: {
  readonly events: readonly AccountEvent[];
  readonly alerts: readonly Alert[];
  readonly currentAlertId: string;
  readonly onOpenAlert: (id: string) => void;
}) {
  const [showAll, setShowAll] = useState(false);
  const newestFirst = [...events].sort((a, b) => b.at - a.at);
  const isCollapsed = !showAll && newestFirst.length > LATEST_N;
  const shown = isCollapsed ? newestFirst.slice(0, LATEST_N) : newestFirst;
  const oldestShownAt = shown.at(-1)?.at;
  const visibleAlerts =
    isCollapsed && oldestShownAt !== undefined ? alerts.filter((a) => a.first_seen >= oldestShownAt) : alerts;
  return (
    <>
      {events.length === 0 && (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {NO_EVENTS}
        </Typography>
      )}
      {entriesByDay(shown, visibleAlerts).map(([day, entries]) => (
        <DayGroup key={day} day={day} entries={entries} currentAlertId={currentAlertId} onOpenAlert={onOpenAlert} />
      ))}
      {newestFirst.length > LATEST_N && (
        <Button size="small" onClick={() => setShowAll((value) => !value)}>
          {showAll ? `Show the latest ${LATEST_N}` : `Show all ${newestFirst.length} events`}
        </Button>
      )}
    </>
  );
}
